import React, { useState } from 'react';
import { Modal, ModalHeader, ModalFooter, ModalBody, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button } from '@/components/Buttons';
import { ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { addCommentThread, getPossibleChatFollowers } from '@/services/comments';
import Banner from '@/components/Banner';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import CreateCommentThreadModalFollowerSelect from './CreateCommentThreadModalFollowerSelect/CreateCommentThreadModalFollowerSelect';
import MaxLengthValidatingInput from '@/components/MaxLengthValidatingInput';
import { PossibleChatFollower, SimplifiedBuyerUser } from '@/types';
import { getResidentialUnitExtraData } from '@/services/residential-units';
import { useIsBuyer, useValidationManager } from '@/utils/hooks';
import {
    addCommentThreadAsBuyer,
    getPossibleChatFollowersAsBuyer,
    getResidentialUnitBuyerUsers,
} from '@/services/buyer-dashboard';
import { Radio } from '@/components/forms/Radio';
import { getUniqueId } from '@/utils/html';

interface CreateCommentThreadModalProps {
    isOpen: boolean;
    toggle(): void;
    residentialUnitId: number;
    onCreate(threadId: number): void;
}

const CreateCommentThreadModal: React.FC<CreateCommentThreadModalProps> = ({
    isOpen,
    toggle,
    residentialUnitId,
    onCreate,
}) => {
    const [threadVisibilityType, setThreadVisibilityType] = useState<'Buyer' | 'Internal' | null>(null);

    const project = useSelector((state: IState) => state.global.activeProject);
    const projectId = project?.id;
    const isBuyer = useIsBuyer();

    const [threadName, setThreadName] = useState('');
    const [selectedFollowerProjectMemberIds, setSelectedFollowerProjectMemberIds] = useState<number[]>([]);
    const [possibleFollowers, setPossibleFollowers] = useState<PossibleChatFollower[] | null>(null);
    const [buyerUsers, setBuyerUsers] = useState<SimplifiedBuyerUser[] | null>(null);
    const includesBuyer = threadVisibilityType === 'Buyer';

    const validationManager = useValidationManager([
        {
            isValid: (value, options) => threadVisibilityType !== null,
            message: ({ field }) => <Trans>Select either buyer or internal thread</Trans>,
            summaryMessage: ({ field }) => <Trans>Select either buyer or internal thread</Trans>,
        },
    ]);

    const reset = () => {
        setThreadVisibilityType(null);
        setThreadName('');
        setSelectedFollowerProjectMemberIds([]);
    };

    const handleOpen = async () => {
        reset();

        const [members, bUsers] = await Promise.all([
            isBuyer
                ? getPossibleChatFollowersAsBuyer(residentialUnitId)
                : getPossibleChatFollowers(projectId!, includesBuyer),
            isBuyer
                ? getResidentialUnitBuyerUsers(residentialUnitId)
                : (await getResidentialUnitExtraData(residentialUnitId)).buyerInfo.users,
        ]);

        setPossibleFollowers(members);
        setBuyerUsers(bUsers);

        if (isBuyer) {
            setThreadVisibilityType('Buyer');
        }

        // const currentMember = members.find((x) => x.applicationUserId === currentUserId);
        // if (currentMember) {
        //     setSelectedFollowerProjectMemberIds([currentMember.projectMemberId]);
        // }
    };

    const handleCreate = async () => {
        const threadId = isBuyer
            ? await addCommentThreadAsBuyer(residentialUnitId, threadName, selectedFollowerProjectMemberIds)
            : await addCommentThread(residentialUnitId, threadName, includesBuyer, selectedFollowerProjectMemberIds);
        onCreate(threadId);
        toggle();
    };

    const radioName = getUniqueId();

    const renderContent = () => {
        if ((!project && !isBuyer) || !possibleFollowers || !buyerUsers) {
            return <LoadingIndicator />;
        }

        return (
            <>
                <div className="form columns-1">
                    <label>
                        <Trans>Title</Trans>
                        <MaxLengthValidatingInput
                            onChange={(e) => setThreadName(e.target.value)}
                            value={threadName}
                            required
                            className="form-control"
                            fieldName={t`Title`}
                            placeholder={t`What would you like to discuss in this thread?`}
                            maxLength={80}
                        />
                    </label>
                    {!isBuyer && (
                        <div className="form-label">
                            <div>
                                <Radio
                                    name={radioName}
                                    disabled={!project!.permissions.canCreateBuyerUnitChatThreads}
                                    onChange={(e) => setThreadVisibilityType('Buyer')}
                                    checked={threadVisibilityType === 'Buyer'}>
                                    <Trans>Buyer thread</Trans>
                                </Radio>
                            </div>
                            <div>
                                <Radio
                                    name={radioName}
                                    disabled={!project!.permissions.canCreateInternalUnitChatThreads}
                                    onChange={(e) => setThreadVisibilityType('Internal')}
                                    checked={threadVisibilityType === 'Internal'}>
                                    <Trans>Internal thread</Trans>
                                </Radio>
                            </div>
                        </div>
                    )}

                    <label>
                        <Trans>Followers</Trans>

                        <CreateCommentThreadModalFollowerSelect
                            buyers={includesBuyer ? buyerUsers : []}
                            value={selectedFollowerProjectMemberIds}
                            onChange={setSelectedFollowerProjectMemberIds}
                            users={possibleFollowers}
                        />
                    </label>
                    <ValidationSummary />
                </div>

                <Banner type="info">
                    <Trans>You can add a user to a thread by tagging them in a message by using '@'.</Trans>
                </Banner>
            </>
        );
    };

    return (
        <Modal onClose={reset} size="lg" isOpen={isOpen} toggle={toggle} onOpen={handleOpen}>
            <ValidationForm validationManager={validationManager} onSubmit={handleCreate}>
                <ModalHeader>
                    <Trans>Create a new message thread</Trans>
                    {/* {isBuyer ? (
                    ) : includesBuyer ? (
                        <Trans>Create a new buyer message thread</Trans>
                    ) : (
                        <Trans>Create a new internal message thread</Trans>
                    )} */}
                </ModalHeader>

                <ModalBody>{renderContent()}</ModalBody>
                <ModalFooter>
                    <Button color="tertiary" onClick={toggle}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" color="primary">
                        <Trans>Create</Trans>
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default CreateCommentThreadModal;
